/*
 * vms-assembly-art.js
 * ---------------------------------------------------------------------------
 * Composites a to-scale side elevation of a COMPLETE ceiling mount: ceiling
 * plate, pipe mast, mounting head, drawn as one picture from the per-part
 * orthographic cutouts in Shopify Files (custom.ortho_image) plus the pipe
 * sprite that vms-mast-art.js already owns.
 *
 * Standalone on purpose, like its sibling: window.VMSAssemblyArt has no
 * dependency on the configurator, so a PDP or a future kit builder can call it.
 * It DELEGATES the whole mast to window.VMSMastArt rather than reimplementing
 * pipes, so the one-inch-column model and the coupler overlap stay in one place.
 *
 * ===========================================================================
 * THE PLACEMENT RULE. Owner-set 2026-08-19. Read this before changing any math.
 * ===========================================================================
 *
 * NOTHING HERE INSPECTS PIXELS TO FIND OR CONFIRM AN OFFSET. The offset is
 * owner-MEASURED data, stored as custom.length_offset. It is an input. The
 * predecessor tool (scripts/ortho-cutouts/process_orthos.py) scanned the render
 * to locate the socket and derive geometry from it; that was retired by owner
 * ruling after it centred three heads on a column of bolt holes, off by 7.878
 * in identically. Its replacement is scripts/ortho-cutouts/placement.py, and
 * this file is the browser-side twin of that module. Keep them in step.
 *
 * The rule, in the owner's words:
 *   1. "Is where the pipe contacts the head at the top of the ortho, then
 *       overlay .35"
 *   2. "if the place where the pipe contacts that head is not at the top, take
 *       1/2 the image height and apply the offset"
 *
 *   contact AT the image edge ->  overlap = 0.350                (flat constant)
 *   contact RECESSED          ->  overlap = imageHeightIn/2 - length_offset,
 *                                 but never LESS than 0.350 (the floor, below)
 *
 * The only thing read from a cutout is its intrinsic HEIGHT, which is a
 * property of the file, not an inspection of the drawing.
 *
 * THE FLOOR. Owner ruling 2026-09-18, verbatim: "nothing should draw flush
 * against the pipe they should have the overlap". So a head overlaps the pipe
 * end by AT LEAST the flat 0.350 in edge overlay, exactly as a plate does. The
 * half-height rule is a SHORTCUT that assumes the VESA hole-pattern centre sits
 * at half the cutout height. That is wrong on any head whose centre is off
 * mid-height: 22HE001 and 22HE021 are 9.9 in tall with the centre 0.62 in BELOW
 * the middle, so half-height minus their 5.05 in offset comes out slightly
 * NEGATIVE and, unfloored, the head would butt flush against the pipe end. The
 * floor in overlapIn() catches exactly that: when the shortcut yields less than
 * 0.350 (including a negative), the head takes the flat 0.350 instead. It is
 * now expected, correct data, not an anomaly, so it is applied SILENTLY (no
 * warning): 22HE001 and 22HE021 hit it on every build.
 *
 * WHICH CASE A PART IS IN is derived from component_type, and that derivation
 * is not a guess: it was checked against the whole installed pool on
 * 2026-08-19. All 14 ceiling plates and adapters contact at the image edge
 * (their socket sits on the bottom edge by the cutout spec), and all 9 heads
 * with cutouts have a RECESSED socket boss (33HE001 row 301, 33HE002 row 300,
 * PDS-LCHB row 533 of their own content). So:
 *
 *   component_type 'ceiling_plate' -> contact at edge -> 0.350
 *   component_type 'mounting_head' -> recessed        -> half-height rule,
 *                                                        floored at 0.350
 *
 * sanityCheckOverlap() below is now the UPPER guard only: the floor makes a
 * negative or near-zero overlap impossible, so the one case left to refuse is
 * an overlap DEEPER than the part itself, which still means a bad length_offset
 * or a mis-derived contact case and still must not draw silently.
 *
 * VALIDATION against the numbers CAD had settled before the rule replaced it:
 *   33HE001   3.915 vs 3.911   (+0.004)
 *   PDS-LCHB  5.911 vs 5.898   (+0.013)
 *   33HE021   3.973 vs 3.911   (+0.062)
 * All inside the representational bar. 22HE021's CAD 0.819 was retired earlier
 * (owner: "22HE021 cad is likely wrong. Go with the 4.75 measured"); with the
 * length_offset now 5.05 (owner 2026-09-18) the half-height shortcut goes
 * negative, so the head takes the 0.350 floor rather than any CAD figure.
 *
 * ACCURACY BAR, owner ruling: representational, not metrological. "Nobody
 * expects pixel perfect images that could be used for reference measurements.
 * Close is good enough." Do not add sub-millimetre corrections here.
 *
 * ===========================================================================
 * INHERITED CONSTRAINTS. These are not style choices.
 * ===========================================================================
 *
 * NEVER DRAW AT NATIVE SCALE. The cutouts are 94 px/in and large: the widest
 * measured is 33CE008HEAD/33CE018HEAD at 43.86 in, which is 4123 px wide
 * natively, and PDS-LCHB is 2070 px tall. A scratch canvas sized to hold a
 * full assembly at native scale blows past iOS Safari's ~16.7 MP canvas cap
 * and renders BLANK on iPhones. Everything here is drawn straight to the
 * display-size canvas at the assembly's own px-per-inch. Same reason
 * vms-mast-art.js gives at its point 4, and it binds harder here because this
 * drawing is wider than that one.
 *
 * DPR CAPPED AT 2, as vms-mast-art.js:242 does.
 *
 * DRAW ORDER IS STRICT: mast first, then plate, then head. Parts paint OVER
 * the pipe. That is what lets a recessed socket swallow the pipe end and what
 * makes 33CE008HEAD work at all, since on that part the pipe passes BEHIND the
 * bracket for most of its height rather than into a socket.
 *
 * PER PART SCALE AND CLIP. Cutout widths vary hugely and several are
 * ASYMMETRIC about the pipe axis (IBA-1's strap). Every part is positioned by
 * its own axis on the mast axis and clipped to the canvas. Never assume a
 * symmetric bounding box and never assume a maximum width.
 */
(function () {
  'use strict';

  /** Owner ruling: every plate and clamp adapter overlays the pipe end by this. */
  var EDGE_OVERLAY_IN = 0.350;

  /**
   * Owner ruling: draw the pipe at its 1.900 in OUTSIDE diameter. The 1.750 in
   * figure throughout the CAD is the tap-drill bore, a hidden joint interior.
   * NOTE the pipe sprite's own band is 1.936 in (182 px at 94 ppi), which is
   * what VMSMastArt actually draws. The 0.036 in disagreement is under a third
   * of a pixel at any scale this renders at and is deliberately left alone
   * under the representational bar; it is recorded so nobody "discovers" it
   * later and starts reconciling sprite geometry.
   */
  var PIPE_OD_IN = 1.900;

  var NATIVE_PPI = 94;

  // ---- cutout loading, one shared promise PER URL ---------------------------
  // vms-mast-art.js caches a single promise because it has exactly one sprite.
  // This module loads many different files, so the cache is keyed by URL.
  var imageCache = new Map();

  function loadCutout(url) {
    if (!url) return Promise.reject(new Error('vms-assembly-art: no cutout url'));
    var hit = imageCache.get(url);
    if (hit) return hit;
    var p = new Promise(function (resolve, reject) {
      var img = new Image();
      // The cutouts live on the Shopify Files CDN, a different origin from the
      // theme assets, so this is load-bearing here in a way it is not for the
      // sprite: without it the canvas is tainted and any readback throws.
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        if (!img.naturalWidth || !img.naturalHeight) {
          reject(new Error('vms-assembly-art: cutout decoded to zero size ' + url));
          return;
        }
        resolve(img);
      };
      img.onerror = function () {
        // Drop the rejected promise so a later attempt can retry rather than
        // inheriting a permanent failure for the life of the page.
        imageCache.delete(url);
        reject(new Error('vms-assembly-art: cutout failed to load ' + url));
      };
      img.src = url;
    });
    imageCache.set(url, p);
    return p;
  }

  // ---- the placement rule ---------------------------------------------------

  /**
   * True when the pipe meets this part right at the image edge, which is the
   * flat 0.350 case. Derived from component_type; see the header for why that
   * derivation is sound across the current pool and how it could break.
   */
  function contactAtEdge(componentType) {
    return componentType === 'ceiling_plate';
  }

  /**
   * How far the part overlaps the pipe end, in inches.
   * @param {string} componentType 'ceiling_plate' or 'mounting_head'
   * @param {number} imageHeightPx intrinsic height of the cutout
   * @param {number} lengthOffsetIn custom.length_offset, owner-measured
   */
  function overlapIn(componentType, imageHeightPx, lengthOffsetIn) {
    if (contactAtEdge(componentType)) return EDGE_OVERLAY_IN;
    // Half-height shortcut, FLOORED at the flat edge overlay. Owner 2026-09-18:
    // "nothing should draw flush against the pipe they should have the overlap."
    // The shortcut assumes the hole-pattern centre is at mid-height; on a head
    // where it is not (22HE001, 22HE021: centre 0.62 in below the middle) the
    // result drops below 0.350 or goes negative, so the head is floored to the
    // same flat 0.350 a plate gets. Applied silently: for those heads it is the
    // expected value, not an error, so it must not spam the console on a build.
    var raw = (imageHeightPx / NATIVE_PPI) / 2 - lengthOffsetIn;
    return raw < EDGE_OVERLAY_IN ? EDGE_OVERLAY_IN : raw;
  }

  /**
   * Refuse to draw an overlap DEEPER than the part itself silently.
   * Returns the overlap to use, clamped, plus a warning string when it moved.
   * The LOW end can no longer be wrong: overlapIn() floors a head at 0.350 and a
   * plate is the flat 0.350, so a negative or near-zero overlap cannot reach
   * here (the old "clamp negative to 0" branch was removed with that floor,
   * 2026-09-18). What is left is an overlap larger than the whole part, which
   * still means a bad length_offset or a mis-derived contact case and still
   * must not draw silently.
   */
  function sanityCheckOverlap(sku, ov, partHeightIn) {
    if (ov > partHeightIn) {
      return { overlap: partHeightIn, warn: sku + ': overlap computed ' + ov.toFixed(3) +
        ' in on a part only ' + partHeightIn.toFixed(3) + ' in tall. Clamped to the part height.' };
    }
    return { overlap: ov, warn: null };
  }

  // ---- geometry -------------------------------------------------------------

  /**
   * Lay the assembly out on the ONE reference the rest of the configurator
   * already uses (assets/vms-elevation.js:13-29): every depth is inches
   * measured DOWN from the building structure, which is depth 0.
   *
   *   depth 0                       structure
   *   plate.length_offset           pipe top, where the mast starts
   *   dropIn                        the hole pattern, middle of the screen
   *   dropIn - head.length_offset   pipe bottom
   *
   * Returns pure numbers. No canvas, no images, so callers can size a slot and
   * tests can assert against it before anything has loaded.
   */
  function layout(assembly) {
    var plate = assembly.plate, head = assembly.head;
    var warns = [];
    var out = {
      structureDepthIn: 0,
      dropIn: assembly.dropIn,
      plate: null,
      head: null,
      mast: null
    };

    var pipeTop = plate ? plate.lengthOffsetIn : 0;
    var pipeBottom = head ? assembly.dropIn - head.lengthOffsetIn : assembly.dropIn;

    if (plate) {
      var pH = plate.heightPx / NATIVE_PPI;
      var pOv = overlapIn('ceiling_plate', plate.heightPx, plate.lengthOffsetIn);
      var pChk = sanityCheckOverlap(plate.sku, pOv, pH);
      if (pChk.warn) warns.push(pChk.warn);
      // The plate sits ABOVE the pipe top and reaches 0.350 in past it, so the
      // collar swallows the pipe end.
      var pBottom = pipeTop + pChk.overlap;
      out.plate = {
        sku: plate.sku,
        topDepthIn: pBottom - pH,
        bottomDepthIn: pBottom,
        heightIn: pH,
        widthIn: plate.widthPx / NATIVE_PPI,
        overlapIn: pChk.overlap
      };
    }

    if (head) {
      var hH = head.heightPx / NATIVE_PPI;
      var hOv = overlapIn('mounting_head', head.heightPx, head.lengthOffsetIn);
      var hChk = sanityCheckOverlap(head.sku, hOv, hH);
      if (hChk.warn) warns.push(hChk.warn);
      // The head hangs BELOW the pipe bottom, starting `overlap` above it so
      // the pipe end is hidden inside (or behind) the part.
      var hTop = pipeBottom - hChk.overlap;
      out.head = {
        sku: head.sku,
        topDepthIn: hTop,
        bottomDepthIn: hTop + hH,
        heightIn: hH,
        widthIn: head.widthPx / NATIVE_PPI,
        overlapIn: hChk.overlap
      };
    }

    out.mast = { topDepthIn: pipeTop, bottomDepthIn: pipeBottom,
                 lengthIn: Math.max(0, pipeBottom - pipeTop) };

    // The drawing spans from the highest thing to the lowest. The plate can sit
    // slightly ABOVE depth 0 (its own body is taller than its offset plus the
    // overlay), and on a long-drop build the head reaches well past the drop.
    var top = 0, bottom = assembly.dropIn;
    if (out.plate) { top = Math.min(top, out.plate.topDepthIn); bottom = Math.max(bottom, out.plate.bottomDepthIn); }
    if (out.head)  { top = Math.min(top, out.head.topDepthIn);  bottom = Math.max(bottom, out.head.bottomDepthIn); }
    bottom = Math.max(bottom, out.mast.bottomDepthIn);
    out.extentTopIn = top;
    out.extentBottomIn = bottom;
    out.totalHeightIn = bottom - top;

    // Widest element decides the drawing's width. NEVER assume symmetry: a part
    // can hang further one side of the axis than the other (IBA-1's strap), so
    // this is a half-width on each side taken independently where known.
    var halfMax = PIPE_OD_IN / 2;
    if (out.plate) halfMax = Math.max(halfMax, out.plate.widthIn / 2);
    if (out.head)  halfMax = Math.max(halfMax, out.head.widthIn / 2);
    out.halfWidthIn = halfMax;
    out.totalWidthIn = halfMax * 2;
    out.warnings = warns;
    return out;
  }

  // ---- drawing --------------------------------------------------------------

  function drawCutout(ctx, img, axisXPx, topPx, ppi) {
    // Positioned by ITS OWN axis on the mast axis. The cutouts are padded so the
    // pipe axis is the exact horizontal centre of the file, which is what makes
    // this a centre-anchored draw and not a per-part nudge table.
    var wPx = (img.naturalWidth / NATIVE_PPI) * ppi;
    var hPx = (img.naturalHeight / NATIVE_PPI) * ppi;
    ctx.drawImage(img, Math.round(axisXPx - wPx / 2), Math.round(topPx),
                  Math.max(1, Math.round(wPx)), Math.max(1, Math.round(hPx)));
  }

  /**
   * Render a full assembly onto a canvas.
   *
   * @param {HTMLCanvasElement} canvas
   * @param {Object} assembly
   *        dropIn {number}  finished drop, structure to hole pattern
   *        plate  {sku, ortho, lengthOffsetIn, widthPx, heightPx} or null
   *        head   {sku, ortho, lengthOffsetIn, widthPx, heightPx} or null
   *        mast   {Array}   a VMSMastArt pieces spec, or null for no pipe
   * @param {Object} opts
   *        spriteUrl {string} required when a mast is present
   *        widthPx   {number} css width of the drawing box
   *        heightPx  {number} css height of the drawing box
   *        maxPpi    {number} px-per-inch ceiling. Default 24, matching the
   *                           elevation scene, so a short mount does not render
   *                           as a giant.
   * @returns {Promise<{ppi, layout, widthPx, heightPx, warnings}>}
   */
  function render(canvas, assembly, opts) {
    opts = opts || {};
    if (!canvas || !assembly) {
      return Promise.reject(new Error('vms-assembly-art: nothing to render'));
    }

    var L = layout(assembly);
    if (!(L.totalHeightIn > 0)) {
      return Promise.reject(new Error('vms-assembly-art: assembly has no height'));
    }

    // [E] Generation token per render(), keyed on the target canvas. Two renders
    // of the SAME card canvas can be in flight at once (a plate or head change
    // fires a re-render before the previous head's cutout has finished loading),
    // and Promise.all below resolves in fetch-completion order, not call order. A
    // slow fetch for an older selection could otherwise resolve last and paint a
    // stale composite over the newer one. Each call claims the next token; the
    // paint pass bails without touching the canvas if a newer call has since
    // superseded it. The synchronous sizing below still runs so a lone render is
    // unaffected; only the async paint is gated.
    var myToken = (canvas.__vmsAsmGen = (canvas.__vmsAsmGen || 0) + 1);

    var boxW = Math.max(1, Math.round(opts.widthPx || 320));
    var boxH = Math.max(1, Math.round(opts.heightPx || 420));
    var maxPpi = opts.maxPpi || 24;

    // Fit the whole assembly in the box, both axes, and never magnify past the
    // ceiling. Same shape as the elevation scene's own scale rule.
    var ppi = Math.min(boxH / L.totalHeightIn, boxW / L.totalWidthIn, maxPpi);

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(boxW * dpr);
    canvas.height = Math.round(boxH * dpr);
    canvas.style.width = boxW + 'px';
    canvas.style.height = boxH + 'px';

    var axisX = boxW / 2;
    var depthToY = function (d) { return (d - L.extentTopIn) * ppi; };

    var jobs = [];
    // The mast is delegated whole to VMSMastArt, drawn onto its own offscreen
    // canvas at the SAME ppi and then blitted in. That keeps the one-inch-column
    // model and the coupler pass in exactly one place.
    var mastCanvas = null;
    if (assembly.mast && assembly.mast.length && window.VMSMastArt && L.mast.lengthIn > 0) {
      mastCanvas = document.createElement('canvas');
      // [WARN] maxPpi IS THE PPI THE REQUESTED LENGTH IMPLIES, NOT OUR SCENE PPI,
      // and the difference is a real bug, not a nicety. render() computes
      // min(lengthPx / totalIn, maxPpi) where totalIn is the length of the SPEC,
      // while lengthPx above is the GEOMETRIC span between the plate and the head.
      // Those disagree by up to about 0.75 in, because buildMasts accepts a build
      // whose range misses the mast target by TOL and specFromBuild then clamps to
      // that range. Whenever the span is the longer of the two, passing our own ppi
      // caps the draw back to the spec's length and the mast lands short of the
      // head. Handing over the exact ratio makes the cap a no-op and pins the
      // canvas to lengthPx; when the two lengths agree it IS our ppi, unchanged.
      // Same fix and same reasoning as layoutMast in assets/vms-elevation-scene.js;
      // keep the two in step.
      var mastSpanPx = L.mast.lengthIn * ppi;
      var mastSpecIn = window.VMSMastArt.totalInches(assembly.mast);
      jobs.push(window.VMSMastArt.render(mastCanvas, assembly.mast, {
        spriteUrl: opts.spriteUrl,
        lengthPx: mastSpanPx,
        maxPpi: mastSpecIn > 0 ? mastSpanPx / mastSpecIn : ppi,
        vertical: true
      }).catch(function (e) { mastCanvas = null; return null; }));
    }

    var plateImg = null, headImg = null;
    if (assembly.plate && assembly.plate.ortho) {
      jobs.push(loadCutout(assembly.plate.ortho).then(function (i) { plateImg = i; })
        .catch(function () { plateImg = null; }));
    }
    if (assembly.head && assembly.head.ortho) {
      jobs.push(loadCutout(assembly.head.ortho).then(function (i) { headImg = i; })
        .catch(function () { headImg = null; }));
    }

    return Promise.all(jobs).then(function () {
      // [E] A newer render() for this canvas claimed a later token while our fetches
      // were outstanding. Drop this stale pass without drawing, so the newer, more
      // recent composite is the one that stays on screen.
      if (canvas.__vmsAsmGen !== myToken) {
        return { ppi: ppi, layout: L, widthPx: boxW, heightPx: boxH, warnings: L.warnings, stale: true };
      }
      var ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);

      // STRICT ORDER: mast, then plate, then head. Parts paint over the pipe.
      if (mastCanvas) {
        // VMSMastArt sets its canvas CSS size in px; read it back as NUMBERS.
        // (Doing arithmetic straight on the style string coerces via JS string
        // rules and silently yields NaN for the half-width.)
        var mW = parseFloat(mastCanvas.style.width) || 0;
        var mH = parseFloat(mastCanvas.style.height) || 0;
        if (mW > 0 && mH > 0) {
          ctx.drawImage(mastCanvas,
            Math.round(axisX - mW / 2),
            Math.round(depthToY(L.mast.topDepthIn)),
            Math.round(mW), Math.round(mH));
        }
      }
      if (plateImg && L.plate) drawCutout(ctx, plateImg, axisX, depthToY(L.plate.topDepthIn), ppi);
      if (headImg && L.head) drawCutout(ctx, headImg, axisX, depthToY(L.head.topDepthIn), ppi);

      if (L.warnings.length && window.console && console.warn) {
        for (var i = 0; i < L.warnings.length; i++) console.warn('vms-assembly-art: ' + L.warnings[i]);
      }
      return { ppi: ppi, layout: L, widthPx: boxW, heightPx: boxH, warnings: L.warnings };
    });
  }

  /**
   * All-or-nothing gate, per spec 7.4. A partial composite would put an
   * orthographic cutout next to a 3/4 angle photograph in one drawing, which is
   * the collage the 2026-07-26 ruling refused. Missing cutouts are a permanent
   * steady state (two heads are holdouts by decision, 33CE004HEAD and
   * LCD-MID-CHB may never get one), not a temporary gap.
   */
  function canRender(assembly) {
    if (!assembly) return false;
    if (assembly.plate && !assembly.plate.ortho) return false;
    if (assembly.head && !assembly.head.ortho) return false;
    return !!(assembly.plate || assembly.head);
  }

  window.VMSAssemblyArt = {
    render: render,
    layout: layout,
    canRender: canRender,
    overlapIn: overlapIn,
    contactAtEdge: contactAtEdge,
    EDGE_OVERLAY_IN: EDGE_OVERLAY_IN,
    PIPE_OD_IN: PIPE_OD_IN,
    NATIVE_PPI: NATIVE_PPI,
    _internals: { loadCutout: loadCutout, sanityCheckOverlap: sanityCheckOverlap,
                  imageCache: imageCache }
  };
})();
